import React, { useState, useEffect, useCallback } from 'react';
import { FiX, FiUser, FiCalendar, FiClock } from 'react-icons/fi';
import api from '../services/api';
import { getTodayIST, addDaysIST, formatDate } from '../utils/dateUtils';


function BookingForm({ isOpen, onClose, onSuccess, category, availableServices, lockCategory = false }) {
  const [guests, setGuests] = useState([]);
  const [guestType, setGuestType] = useState('resort_guest'); // 'resort_guest' or 'non_staying_guest'
  const [formData, setFormData] = useState({
    guest_name: '',
    guest_phone: '',
    guest_room_no: '',
    guest_gender: '',
    num_people: 1,
    category: category || '',
    service_type: '',
    booking_date: '',
    booking_time: ''
  });
  const [availableDates, setAvailableDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDates, setLoadingDates] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedBookedSlot, setSelectedBookedSlot] = useState(null);

  const fetchGuests = async () => {
    try {
      const response = await api.get('/api/admin/guests');
      setGuests(response.data);
    } catch (error) {
      console.error('Failed to fetch guests:', error);
    }
  };

  const buildUpcomingDates = (daysToRender = 21) => {
    const days = [];
    const today = getTodayIST();
    for (let i = 0; i < daysToRender; i++) {
      const date = addDaysIST(today, i);
      const dateStr = formatDate(date, 'yyyy-MM-dd');
      const dayName = formatDate(date, 'EEE');
      const month = formatDate(date, 'MMM');
      const day = date.getDate();
      days.push({
        id: dateStr,
        date: dateStr,
        display: `${dayName}, ${month} ${day}`,
        fullDisplay: formatDate(date, 'EEEE, MMMM d, yyyy')
      });
    }
    return days;
  };

  const fetchAvailableDates = useCallback(async (category) => {
    if (!category) return;
    
    try {
      setLoadingDates(true);
      const response = await api.get(`/api/slots/${category}`);
      const { daySlots } = response.data || {};
      const upcoming = buildUpcomingDates(21);
      const enabledDates = upcoming.filter((dateObj) => {
        const dateRule = daySlots?.[dateObj.date];
        return !dateRule || dateRule.enabled !== false;
      });
      setAvailableDates(enabledDates);
    } catch (error) {
      console.error('Failed to fetch available dates:', error);
      setAvailableDates([]);
    } finally {
      setLoadingDates(false);
    }
  }, []);

  const generateTimeSlotsFromWorkingHours = (workingHours) => {
    if (!workingHours?.start_time || !workingHours?.end_time) return [];
    const slots = [];
    const [startHour, startMinute] = String(workingHours.start_time).split(':').map(Number);
    const [endHour, endMinute] = String(workingHours.end_time).split(':').map(Number);
    const durationMinutes = Math.max(15, Number(workingHours.duration_minutes) || 60);
    const startMinutes = (startHour * 60) + startMinute;
    const endMinutes = (endHour * 60) + endMinute;

    for (let current = startMinutes; current + durationMinutes <= endMinutes; current += durationMinutes) {
      const next = current + durationMinutes;
      const startH = Math.floor(current / 60);
      const startM = current % 60;
      const endH = Math.floor(next / 60);
      const endM = next % 60;
      const timeStr = `${String(startH).padStart(2, '0')}:${String(startM).padStart(2, '0')}`;
      const startDisplay = new Date(2000, 0, 1, startH, startM).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      const endDisplay = new Date(2000, 0, 1, endH, endM).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      slots.push({
        id: timeStr,
        title: startDisplay,
        description: `${startDisplay} - ${endDisplay}`
      });
    }

    return slots;
  };

  const fetchAvailableSlots = useCallback(async (category, date) => {
    if (!category || !date) return;
    
    try {
      setLoadingSlots(true);
      const response = await api.get(`/api/slots/${category}`);
      const { timeSlots: savedTimeSlots, workingHours } = response.data || {};
      const rawSlots = generateTimeSlotsFromWorkingHours(workingHours);
      const enabledTimeSlots = rawSlots.filter((slot) => {
        const timeRule = savedTimeSlots?.[slot.id];
        return !timeRule || timeRule.enabled !== false;
      });

      try {
        const bookingsResponse = await api.get('/api/roomservice/requests', {
          params: {
            category,
            status: 'received,completed'
          }
        });

        const bookedSlots = bookingsResponse.data.filter((booking) =>
          booking.metadata?.booking_date === date &&
          ['pending', 'received', 'in-progress', 'completed'].includes(booking.status)
        );
        const bookedByTime = bookedSlots.reduce((acc, booking) => {
          const time = booking.metadata?.booking_time;
          if (!time) return acc;
          if (!acc[time]) acc[time] = [];
          acc[time].push(booking);
          return acc;
        }, {});
        const todayIST = formatDate(getTodayIST(), 'yyyy-MM-dd');
        const isToday = date === todayIST;
        let filteredSlots = enabledTimeSlots;

        if (isToday) {
          const now = new Date();
          const istOffset = 5.5 * 60 * 60 * 1000;
          const istNow = new Date(now.getTime() + istOffset);
          const currentTimeMinutes = (istNow.getUTCHours() * 60) + istNow.getUTCMinutes();
          filteredSlots = filteredSlots.filter((slot) => {
            const [slotHour, slotMinute] = slot.id.split(':').map(Number);
            return (slotHour * 60) + slotMinute > currentTimeMinutes;
          });
        }

        const capacity = workingHours?.capacity || 1;
        filteredSlots = filteredSlots.map((slot) => {
          const slotBookings = bookedByTime[slot.id] || [];
          const bookedCount = slotBookings.reduce((sum, b) => {
            return sum + Number(b.metadata?.num_people || b.metadata?.number_of_people || 1);
          }, 0);
          return {
            ...slot,
            bookedCount,
            capacity,
            isBooked: bookedCount >= capacity,
            bookings: slotBookings
          };
        });
        setAvailableSlots(filteredSlots);
      } catch (bookingError) {
        console.warn('Failed to check booked slots, showing all enabled slots:', bookingError);
        setAvailableSlots(enabledTimeSlots.map((slot) => ({ ...slot, isBooked: false, bookedCount: 0, capacity: workingHours?.capacity || 1, bookings: [] })));
      }
    } catch (error) {
      console.error('Failed to fetch available slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchGuests();
      const defaultCategory = formData.category || services[0] || '';
      if (defaultCategory && defaultCategory !== formData.category) {
        setFormData((prev) => ({ ...prev, category: defaultCategory }));
      }
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpen && formData.category) {
      fetchAvailableDates(formData.category);
    }
  }, [isOpen, formData.category, fetchAvailableDates]);

  useEffect(() => {
    if (formData.category && selectedDate) {
      fetchAvailableSlots(formData.category, selectedDate);
    } else {
      setAvailableSlots([]);
    }
  }, [formData.category, selectedDate, fetchAvailableSlots]);

  const handleDateSelect = (dateObj) => {
    setSelectedDate(dateObj.date);
    setSelectedBookedSlot(null);
    setFormData({ ...formData, booking_date: dateObj.date, booking_time: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate based on guest type
    const isServiceTypeRequired = formData.category === 'Spa' || formData.category === 'Salon';
    if (!formData.guest_name || !formData.guest_phone || !formData.guest_gender || !formData.category || !formData.booking_date || !formData.booking_time || (isServiceTypeRequired && !formData.service_type)) {
      alert('Please fill in all required fields');
      return;
    }
    if (guestType === 'resort_guest' && !formData.guest_room_no.trim()) {
      alert('Room number is required for resort guest.');
      return;
    }
    const phoneDigits = formData.guest_phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      alert('Phone number must be exactly 10 digits');
      return;
    }
    if (!/^[6-9]/.test(phoneDigits)) {
      alert('Indian mobile numbers must start with 6, 7, 8, or 9');
      return;
    }

    try {
      setLoading(true);
      const matchingGuest = guests.find((guest) => {
        const guestPhoneDigits = String(guest.phone || '').replace(/\D/g, '').slice(-10);
        if (guestPhoneDigits !== phoneDigits) return false;
        if (guestType === 'resort_guest') {
          return String(guest.room_no || '').trim() === formData.guest_room_no.trim();
        }
        return true;
      });
      let guestId = matchingGuest?.id;

      if (!guestId) {
        const guestResponse = await api.post('/api/admin/guests', {
          name: formData.guest_name.trim(),
          phone: phoneDigits,
          room_no: formData.guest_room_no.trim() || null
        });
        guestId = guestResponse.data.guest?.id || guestResponse.data.id;
      }
      
      // Create booking
      await api.post('/api/roomservice/booking', {
        guest_id: guestId,
        category: formData.category,
        booking_date: formData.booking_date,
        booking_time: formData.booking_time,
        guest_type: guestType,
        guest_gender: formData.guest_gender,
        num_people: formData.num_people || 1,
        service_type: formData.service_type || null,
        guest_profile: {
          room_no: formData.guest_room_no.trim() || null
        }
      });
      
      onSuccess();
      onClose();
      resetForm();
    } catch (error) {
      console.error('Failed to create booking:', error);
      alert(error.response?.data?.error || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setGuestType('resort_guest');
    setSelectedDate(null);
    setSelectedBookedSlot(null);
    setFormData({
      guest_name: '',
      guest_phone: '',
      guest_room_no: '',
      guest_gender: '',
      num_people: 1,
      category: category || services[0] || '',
      service_type: '',
      booking_date: '',
      booking_time: ''
    });
    setAvailableDates([]);
    setAvailableSlots([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const services = availableServices || ['Yoga', 'Padel', 'PickleBall', 'Trampoline'];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900/50 overflow-y-auto h-full w-full z-50 backdrop-blur-sm">
      <div className="relative top-8 mx-auto p-0 border border-gray-200 w-full max-w-4xl shadow-xl rounded-xl bg-white mb-10 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Create Activity Booking</h3>
              <p className="text-sm text-gray-500 mt-1">Choose guest type and confirm booking using live slot management availability.</p>
            </div>
          <button
            onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <FiX className="h-6 w-6" />
          </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-6">
          <div className="space-y-6">
            {/* Guest Type Selection */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiUser className="inline mr-2" />
                Entry Type
              </label>
              <div className="flex space-x-4 mb-3">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="resort_guest"
                    checked={guestType === 'resort_guest'}
                    onChange={(e) => {
                      setGuestType(e.target.value);
                      setFormData({ ...formData, guest_name: '', guest_phone: '', guest_room_no: '', guest_gender: '' });
                    }}
                    className="mr-2 accent-gray-900"
                  />
                  Resort Guest
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="non_staying_guest"
                    checked={guestType === 'non_staying_guest'}
                    onChange={(e) => {
                      setGuestType(e.target.value);
                      setFormData({ ...formData, guest_name: '', guest_phone: '', guest_room_no: '', guest_gender: '' });
                    }}
                    className="mr-2 accent-gray-900"
                  />
                  Non-staying Guest
                </label>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.guest_name}
                      onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 bg-gray-50 focus:bg-white transition-colors"
                      placeholder="Enter guest name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Number
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.guest_phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setFormData({ ...formData, guest_phone: value });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 bg-gray-50 focus:bg-white transition-colors"
                      placeholder="9876543210"
                      maxLength={10}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Room Number {guestType === 'resort_guest'
                        ? <span className="text-red-500">*</span>
                        : <span className="text-gray-400 font-normal">(Optional)</span>}
                    </label>
                    <input
                      type="text"
                      required={guestType === 'resort_guest'}
                      value={formData.guest_room_no}
                      onChange={(e) => setFormData({ ...formData, guest_room_no: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 bg-gray-50 focus:bg-white transition-colors"
                      placeholder="e.g., 101, A-205"
                    />
                  </div>
                  <div className="md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Gender <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={formData.guest_gender}
                      onChange={(e) => setFormData({ ...formData, guest_gender: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 bg-gray-50 focus:bg-white transition-colors"
                    >
                      <option value="">Select gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Number of People <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={formData.num_people}
                      onChange={(e) => setFormData({ ...formData, num_people: parseInt(e.target.value, 10) || 1 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 bg-gray-50 focus:bg-white transition-colors"
                    >
                      <option value={1}>1 Person</option>
                      <option value={2}>2 People</option>
                      <option value={3}>3 People</option>
                      <option value={4}>4 People (Max)</option>
                    </select>
                  </div>
              </div>
            </div>

            {/* Service Selection */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service
              </label>
              <select
                required
                value={formData.category}
                onChange={(e) => {
                  setFormData({ ...formData, category: e.target.value, booking_date: '', booking_time: '' });
                  setSelectedDate(null);
                  setAvailableSlots([]);
                }}
                disabled={lockCategory}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white"
              >
                {!lockCategory && <option value="">Select service</option>}
              {services.map((service) => (
                <option key={service} value={service}>
                  {service}
                </option>
              ))}
            </select>

            {(formData.category === 'Spa' || formData.category === 'Salon') && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Type (e.g. Deep Tissue, Haircut) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.service_type}
                  onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white"
                  placeholder={`Enter ${formData.category.toLowerCase()} service name`}
                />
              </div>
            )}
          </div>

            {/* Date Selection */}
            {formData.category && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FiCalendar className="inline mr-2" />
                  Select Date
                </label>
                {loadingDates ? (
                  <div className="text-center py-4 text-gray-500">Loading available dates...</div>
                ) : availableDates.length === 0 ? (
                  <div className="text-center py-4 text-red-500">No available dates for this service</div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {availableDates.map((dateObj) => (
                      <button
                        key={dateObj.id}
                        type="button"
                        onClick={() => handleDateSelect(dateObj)}
                        className={`p-3 border-2 rounded-lg text-center transition-all text-sm ${
                          selectedDate === dateObj.date
                            ? 'border-gray-900 bg-gray-900 text-white'
                            : 'border-gray-300 hover:border-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        <div className="text-sm font-medium">{dateObj.display}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Time Slot Selection */}
            {selectedDate && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FiClock className="inline mr-2" />
                  Select Time Slot
                </label>
                {loadingSlots ? (
                  <div className="text-center py-4 text-gray-500">Loading available slots...</div>
                ) : availableSlots.length === 0 ? (
                  <div className="text-center py-4 text-red-500">
                    No available time slots for {availableDates.find(d => d.date === selectedDate)?.fullDisplay || selectedDate}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {availableSlots.map((slot) => (
                      <button
                        key={slot.id}
                        type="button"
                        onClick={() => {
                          if (slot.isBooked) {
                            setSelectedBookedSlot(slot);
                            return;
                          }
                          setSelectedBookedSlot(null);
                          setFormData({ ...formData, booking_time: slot.id });
                        }}
                        className={`p-3 border-2 rounded-lg text-center transition-all ${
                          slot.isBooked
                            ? 'border-amber-300 bg-amber-50 text-amber-900'
                            : formData.booking_time === slot.id
                            ? 'border-gray-900 bg-gray-900 text-white'
                            : 'border-gray-300 hover:border-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        <div className="text-sm font-medium">{slot.title}</div>
                        <div className="text-xs opacity-75">{slot.description}</div>
                        {slot.isBooked ? (
                          <div className="text-[10px] mt-1 font-semibold text-amber-900">
                            Booked ({slot.bookedCount}/{slot.capacity}) - tap for details
                          </div>
                        ) : slot.bookedCount > 0 ? (
                          <div className="text-[10px] mt-1 font-medium text-amber-700">
                            Booked: {slot.bookedCount}/{slot.capacity}
                          </div>
                        ) : null}
                      </button>
                    ))}
                  </div>
                )}
                {selectedBookedSlot?.isBooked && (
                  <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <p className="text-xs font-bold uppercase tracking-wide text-amber-900 mb-2">
                      Booked Slot Details ({selectedBookedSlot.description})
                    </p>
                    <div className="space-y-2">
                      {selectedBookedSlot.bookings.map((booking) => (
                        <div key={booking.id} className="rounded-md border border-amber-200 bg-white p-2 text-xs text-gray-700">
                          <p><span className="font-semibold">Guest:</span> {booking.guest_name || 'Unknown'}</p>
                          <p><span className="font-semibold">Room:</span> {booking.room_no || 'N/A'}</p>
                          <p><span className="font-semibold">Phone:</span> {booking.phone || 'N/A'}</p>
                          <p><span className="font-semibold">Status:</span> {booking.status}</p>
                          <p><span className="font-semibold">Preferences / Details:</span> {booking.details || booking.metadata?.preferences || booking.metadata?.special_request || 'N/A'}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.booking_time}
              className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Booking'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BookingForm;
